/**
 * Objects & Fields Section — standalone settings section.
 *
 * Combines CustomPropertiesCard (with HorizontalTabs) and CustomEntitiesCard.
 * CustomEntitiesCard is gated by `custom_entity_types` feature flag.
 */

import { useQuery } from '@tanstack/react-query';
import { Plus } from '@untitledui/icons';
import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { usePermissions } from '@/hooks/usePermissions';
import { usePropertyPermissions } from '@/hooks/usePropertyPermissions';
import { Button } from '@/lib/base/buttons/button';
import HorizontalTabs from '@/lib/base/tabs/HorizontalTabs';
import type { PropertyType } from '@/lib/crm/core/SchemaClient';
import { createSchemaClient } from '@/lib/crm/core/SchemaClient';
import { useViewHealthMarker } from '@/lib/view-health';

import { useOrg } from '../../../../contexts/OrgContext';
import { DeletePropertyModal } from '../../profile/components/DeletePropertyModal';
import {
  ADMIN_ONLY_PROPERTY_TOOLTIP,
  PropertyTypeList,
} from '../../profile/components/PropertyTypeList';
import { PropertyTypeSlideout } from '../../profile/components/PropertyTypeSlideout';
import { CustomEntitiesCard } from './objects-fields-custom-entities-card';

// ============================================================================
// Custom Properties
// ============================================================================

export function CustomPropertiesContent() {
  const { currentOrg } = useOrg();
  const { canCreate, canEdit, canDelete } = usePropertyPermissions();
  const orgId = currentOrg?.id;

  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');

  const { data: objectTypeTabs = [], isLoading: isLoadingTabs } = useQuery({
    queryKey: ['objectTypes', orgId, 'customProperties'],
    queryFn: async () => {
      if (!orgId) {
        return [];
      }
      const client = createSchemaClient(orgId);
      const objectTypes = await client.getObjectTypes();
      return objectTypes
        .filter(
          t =>
            // Note: Settings view intentionally excludes pipeline/stage object types.
            // CustomPropertiesTab (profile page) shows all allows_custom_properties types.
            t.allows_custom_properties &&
            !t.entity_kind.includes('pipeline') &&
            !t.entity_kind.includes('stage') &&
            // ahoy_note keeps allows_custom_properties: true in the schema (it feeds
            // agent schema rendering + CSV-import property declaration), so we exclude
            // it here at the UI consumer rather than flipping the backend flag —
            // mirroring apps/reasoning/tools/crm/search.py (ttn != "ahoy_note").
            t.entity_type_name !== 'ahoy_note'
        )
        .map(t => ({
          id: t.entity_type_name,
          label: t.label_plural || t.label,
        }));
    },
    enabled: !!orgId,
  });

  const [selectedObjectType, setSelectedObjectType] = useState<string | null>(
    tabParam
  );

  const activeTab = (() => {
    const isSelectedValid = objectTypeTabs.some(
      tab => tab.id === selectedObjectType
    );
    if (isSelectedValid) {
      return selectedObjectType;
    }
    return objectTypeTabs.length > 0 ? objectTypeTabs[0].id : null;
  })();

  const [isSlideoutOpen, setIsSlideoutOpen] = useState(false);
  const [editingProperty, setEditingProperty] = useState<
    PropertyType | undefined
  >(undefined);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingProperty, setDeletingProperty] = useState<PropertyType | null>(
    null
  );

  const handleCreate = useCallback(() => {
    setEditingProperty(undefined);
    setIsSlideoutOpen(true);
  }, []);

  const handleEdit = useCallback((property: PropertyType) => {
    setEditingProperty(property);
    setIsSlideoutOpen(true);
  }, []);

  const handleDelete = useCallback((property: PropertyType) => {
    setDeletingProperty(property);
    setIsDeleteModalOpen(true);
  }, []);

  const handleSlideoutClose = useCallback(() => {
    setIsSlideoutOpen(false);
    setEditingProperty(undefined);
  }, []);

  const handleSlideoutSuccess = useCallback(() => {
    // Slideout will close itself and invalidate the cache
  }, []);

  const handleDeleteModalClose = useCallback(() => {
    setIsDeleteModalOpen(false);
    setDeletingProperty(null);
  }, []);

  const handleDeleteSuccess = useCallback(() => {
    toast.success('Property deleted successfully');
    setIsDeleteModalOpen(false);
    setDeletingProperty(null);
  }, []);

  const handleTabChange = useCallback((tabId: string) => {
    setSelectedObjectType(tabId);
  }, []);

  return (
    <>
      <div>
        <div className='flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4'>
          <h3 className='text-sm font-medium text-fg-primary'>
            Custom properties
          </h3>
          <Button
            size='sm'
            color='primary'
            iconLeading={Plus}
            onClick={handleCreate}
            isDisabled={!canCreate}
            disabledTooltip={ADMIN_ONLY_PROPERTY_TOOLTIP}
            className='self-end sm:self-auto'
          >
            Create property
          </Button>
        </div>

        <div className='space-y-6'>
          {isLoadingTabs ? (
            <div className='flex gap-2'>
              {[1, 2, 3].map(i => (
                <div
                  key={i}
                  className='h-8 w-24 bg-bg-tertiary rounded animate-pulse'
                />
              ))}
            </div>
          ) : (
            <HorizontalTabs
              tabs={[...objectTypeTabs]}
              activeTab={activeTab ?? ''}
              onTabChange={handleTabChange}
              idPrefix='object-type'
            />
          )}

          <div className='mt-6'>
            {activeTab && (
              <PropertyTypeList
                entityTypeName={activeTab}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onCreate={handleCreate}
                canCreate={canCreate}
                canEdit={canEdit}
                canDelete={canDelete}
              />
            )}
          </div>
        </div>
      </div>

      {activeTab && (
        <PropertyTypeSlideout
          isOpen={isSlideoutOpen}
          onClose={handleSlideoutClose}
          property={editingProperty}
          entityTypeName={activeTab}
          onSuccess={handleSlideoutSuccess}
        />
      )}

      {activeTab && (
        <DeletePropertyModal
          isOpen={isDeleteModalOpen}
          onClose={handleDeleteModalClose}
          property={deletingProperty}
          entityTypeName={activeTab}
          onConfirm={handleDeleteSuccess}
        />
      )}
    </>
  );
}

// ============================================================================
// Objects & Fields Section
// ============================================================================

export function ObjectsFieldsSection() {
  const { hasPermission } = usePermissions();
  const canViewProperties = hasPermission('ahoy_property_type');
  useViewHealthMarker('settings-section-ready');

  return (
    <div className='space-y-8'>
      <div>
        <h2 className='text-lg font-semibold text-fg-primary'>
          Objects & fields
        </h2>
        <p className='text-sm text-fg-tertiary mt-0.5'>
          Manage custom object types and their properties.
        </p>
      </div>
      <div className='space-y-8'>
        <CustomEntitiesCard />
        {canViewProperties && <CustomPropertiesContent />}
      </div>
    </div>
  );
}
